import { useEffect, useState } from "react";
import { DaisyAtlaOption, DaisyAtlaOptions } from "../../models/daisyatlaoption.model";
import { getDaisyAtlaOptionsList } from "../../api/getDaisyAtlaOptionsList";
import { User } from "../../models/user.model";
import GoToUserProfile from "../../components/user/modals/GoToUserProfile";
import AddBonusAtlaModal from "../../components/daisyatlaoptions/AddBonusAtlaModal";

interface OptionVisualConfig {
    label: string;
    classes: string;
}

const OPTION_CONFIG = new Map<string | number | DaisyAtlaOptions, OptionVisualConfig>([
    [DaisyAtlaOptions.A, { label: "1. opció (A)", classes: "bg-slate-50 text-slate-700 ring-1 ring-inset ring-slate-700/10" }],
    ["A",               { label: "1. opció (A)", classes: "bg-slate-50 text-slate-700 ring-1 ring-inset ring-slate-700/10" }],
    [DaisyAtlaOptions.B, { label: "2. opció (B)", classes: "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/10" }],
    ["B",               { label: "2. opció (B)", classes: "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/10" }],
    [DaisyAtlaOptions.C, { label: "3. opció (C)", classes: "bg-pink-50 text-pink-700 ring-1 ring-inset ring-pink-700/10" }],
    ["C",               { label: "3. opció (C)", classes: "bg-pink-50 text-pink-700 ring-1 ring-inset ring-pink-700/10" }]
]);

type SortType = "name" | "option";

export default function DaisyAtlaOptionsPage() {
    const [elements, setElements] = useState<DaisyAtlaOption[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [sortBy, setSortBy] = useState<SortType>("name");

    // Bonus Modal State
    const [isBonusModalOpen, setIsBonusModalOpen] = useState<boolean>(false);
    const [selectedBonusItem, setSelectedBonusItem] = useState<DaisyAtlaOption | null>(null);

    // Stalker Modal State
    const [selectedUser, setSelectedUser] = useState<User | undefined>();
    const [showStalkerModal, setShowStalkerModal] = useState<boolean>(false);

    const openStalkerModal = (user: User) => {
        setSelectedUser(user);
        setShowStalkerModal(true);
    };

    const closeStalkerModal = () => {
        setShowStalkerModal(false);
        setSelectedUser(undefined);
    };

    const openBonusModal = (item: DaisyAtlaOption) => {
        setSelectedBonusItem(item);
        setIsBonusModalOpen(true);
    };

    const closeBonusModal = () => {
        setIsBonusModalOpen(false);
        setSelectedBonusItem(null);
    };

    const handleBonusSuccess = (targetItem: DaisyAtlaOption, newQuantity: string) => {
        setElements(prev =>
            prev.map(el => (el.id === targetItem.id ? { ...el, bonusAtlaQuantity: newQuantity } : el))
        );
    };

    const formatOption = (option: DaisyAtlaOptions | string | number): OptionVisualConfig => {
        return OPTION_CONFIG.get(option) ?? {
            label: "Ismeretlen opció",
            classes: "bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-700/10"
        };
    };

    const loadData = async () => {
        setIsLoading(true);
        try {
            const res = await getDaisyAtlaOptionsList();
            setElements(res);
        } catch (error) {
            console.error("Failed to fetch:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const sortedElements = [...elements].sort((a, b) => {
        if (sortBy === "name") {
            const nameA = a.user?.name ?? "";
            const nameB = b.user?.name ?? "";
            return nameA.localeCompare(nameB, "hu");
        } else {
            const optionOrder: Record<string, number> = {
                "0": 1, "A": 1,
                "1": 2, "B": 2,
                "2": 3, "C": 3,
            };
            const weightA = optionOrder[String(a.option)] ?? 99;
            const weightB = optionOrder[String(b.option)] ?? 99;
            return weightA - weightB;
        }
    });

    return (
        <div className="m-6 md:m-10">
            {/* Top Toolbar with Sorting controls */}
            <div className="mb-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                <div className="text-sm font-medium text-slate-700">
                    Rendezés:
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setSortBy("name")}
                        className={`px-4 py-2 text-xs font-semibold rounded-lg border transition-all ${
                            sortBy === "name"
                                ? "bg-white border-slate-300 text-slate-900 shadow-sm"
                                : "border-transparent text-slate-500 hover:text-slate-800"
                        }`}
                    >
                        Név szerint
                    </button>
                    <button
                        onClick={() => setSortBy("option")}
                        className={`px-4 py-2 text-xs font-semibold rounded-lg border transition-all ${
                            sortBy === "option"
                                ? "bg-white border-slate-300 text-slate-900 shadow-sm"
                                : "border-transparent text-slate-500 hover:text-slate-800"
                        }`}
                    >
                        Opció szerint
                    </button>
                </div>
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center min-h-[200px]">
                    <div className="text-md font-semibold text-slate-600 animate-pulse">
                        Adatok betöltése...
                    </div>
                </div>
            ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
                    <table className="w-full border-collapse text-left text-sm text-slate-600">
                        <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-700 border-b border-slate-200">
                            <tr>
                                <th scope="col" className="px-6 py-4">Felhasználó ID</th>
                                <th scope="col" className="px-6 py-4">Név</th>
                                <th scope="col" className="px-6 py-4">Email</th>
                                <th scope="col" className="px-6 py-4 text-center">Opció</th>
                                <th scope="col" className="px-6 py-4 text-right">Bónusz Mennyiség</th>
                                <th scope="col" colSpan={2} className="px-6 py-4 text-center">Műveletek</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {sortedElements.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-10 text-center text-slate-400">
                                        Nincs megjeleníthető adat.
                                    </td>
                                </tr>
                            ) : (
                                sortedElements.map((item) => {
                                    const bonusNum = parseFloat(item.bonusAtlaQuantity || "0");
                                    const hasBonus = !isNaN(bonusNum) && bonusNum > 0;
                                    const optionStyle = formatOption(item.option);
                                    
                                    // Ellenőrizzük, hogy B vagy C opció-e az érték (enum, string vagy index alapján)
                                    const rawOption = String(item.option);
                                    const isOptionBOrC = 
                                        rawOption === "1" || rawOption === "B" || item.option === DaisyAtlaOptions.B ||
                                        rawOption === "2" || rawOption === "C" || item.option === DaisyAtlaOptions.C;

                                    return (
                                        <tr key={item.id} className="hover:bg-slate-50/70 transition-colors">
                                            <td className="whitespace-nowrap px-6 py-4 font-mono text-xs text-slate-400">
                                                #{item.user?.user_id ?? item.userId}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 font-medium text-slate-900">
                                                {item.user?.name ?? "Ismeretlen felhasználó"}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-slate-500">
                                                {item.user?.email ?? "-"}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-center">
                                                <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold ${optionStyle.classes}`}>
                                                    {optionStyle.label}
                                                </span>
                                            </td>
                                            <td className={`whitespace-nowrap px-6 py-4 text-right font-medium ${
                                                hasBonus ? "text-emerald-600 font-semibold" : "text-slate-400 italic text-xs"
                                            }`}>
                                                {hasBonus ? item.bonusAtlaQuantity : "Nincs beállítva."}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-right">
                                                {isOptionBOrC ? (
                                                    <button
                                                        onClick={() => openBonusModal(item)}
                                                        className="inline-flex items-center rounded-lg bg-pink-50 px-3 py-1.5 text-xs font-medium text-pink-700 ring-1 ring-inset ring-pink-700/20 hover:bg-pink-100 transition-colors"
                                                    >
                                                        ATLA coinok darabszámának megadása
                                                    </button>
                                                ) : (
                                                    <span className="text-xs text-slate-300 italic select-none">Nem elérhető</span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-right">
                                                <p 
                                                    onClick={() => item.user && openStalkerModal(item.user)} 
                                                    className="text-indigo-500 hover:opacity-80 text-md font-bold text-center p-2 cursor-pointer"
                                                >
                                                    Kimutatás
                                                </p>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            )}
            
            <GoToUserProfile 
                title="Átirányítás" 
                user={selectedUser} 
                showModal={showStalkerModal} 
                closeModal={closeStalkerModal}
            />

            <AddBonusAtlaModal
                isOpen={isBonusModalOpen}
                item={selectedBonusItem}
                onClose={closeBonusModal}
                onSuccess={handleBonusSuccess}
            />
        </div>
    );
}